/**
 * #844 — keyboard navigation across the Gutschrift-Check canvas.
 *
 * Pure grid-traversal reducer used by the global ``keydown`` handler in
 * ``MatchGraph`` to move the focused tile across the canvas's 4
 * horizontal columns (Lieferschein ↔ Gutschrift ↔ Abrechnung ↔
 * Zahlungsavis). Operators reviewing 20+ groups per session lose tempo
 * with click-only navigation; arrow keys preserve flow.
 *
 * Pure on purpose: the reducer takes a snapshot of the rendered grid
 * (built once per node-list change in ``MatchGraph``) plus the current
 * selection, and returns the next selection. No React, no DOM —
 * Vitest pins the contract directly without mounting ReactFlow.
 *
 * Traversal rules (pinned by ``__tests__/keyboardNav.test.ts``):
 *   - Up/Down keep the column, change the rowIndex. Skip rows whose
 *     cell in this column is ``null`` (e.g. a row without an
 *     Abrechnung tile because the GS hasn't cited any
 *     ``abrechnungs_number``).
 *   - **#915 — Bestellung is now a vertical sub-cell of the
 *     ``lieferschein`` column** (stacked above the LS tile in the
 *     same x position). It is NOT in ``COLUMN_ORDER``, so left/right
 *     skip it; instead:
 *       - ArrowUp from LS row N → row N's Bestellung if non-null,
 *         else previous row (LS-or-Bestellung, whichever is the
 *         bottom-most tile in the lieferschein column there);
 *       - ArrowDown from Bestellung row N → row N's LS if non-null,
 *         else next row's Bestellung-or-LS (top-most tile);
 *       - ArrowUp from Bestellung row N → previous row's
 *         Bestellung-or-LS (top-most tile in the lieferschein column);
 *       - ArrowLeft/Right from Bestellung use the ``lieferschein``
 *         column as the anchor (so Right → gutschrift, Left → stay).
 *   - Left/Right keep the rowIndex, change the column. Skip columns
 *     whose cell in this row is ``null`` (e.g. orphan_delivery rows
 *     where the Gutschrift column holds a missing-doc placeholder
 *     but no real tile).
 *   - No wrap: hitting a grid edge keeps the current selection. Wrap
 *     would let the operator "fall off" the bottom of the canvas and
 *     land on a row they didn't expect — reducing reading tempo
 *     rather than helping.
 *   - Escape returns ``null`` regardless of the current selection.
 *     Pairs with the existing #835 preview auto-hide so the operator
 *     can clear focus without reaching for the mouse.
 *   - ``current === null`` (fresh canvas, no tile focused yet) on any
 *     arrow key picks the top-left populated cell (row-major scan,
 *     Bestellung-then-LS within the lieferschein column) so the first
 *     press always lands on a real tile.
 *   - Selection pointing at a vanished cell (rematerialise dropped
 *     the row under us): recovers to the top-left populated cell
 *     instead of returning ``null`` — keeps the user navigating
 *     across a refetch boundary.
 */

export type ColumnKey =
  | 'bestellung'
  | 'lieferschein'
  | 'gutschrift'
  | 'abrechnung'
  | 'zahlungsavis';

/** Left-to-right column order on the canvas — matches the
 *  LS → GS → Abrechnung → Zahlungsavis x positions in ``MatchGraph.tsx``
 *  (COL_LEFT = 20, COL_RIGHT = 440, COL_ABRECHNUNG = 860,
 *  COL_ZAHLUNGSAVIS = 1280). #915 — Bestellung was collapsed into the
 *  lieferschein column (stacked above LS at the same x), so it is no
 *  longer a horizontal column for left/right traversal. */
export const COLUMN_ORDER: readonly Exclude<ColumnKey, 'bestellung'>[] = [
  'lieferschein',
  'gutschrift',
  'abrechnung',
  'zahlungsavis',
] as const;

/** Row-major scan order *within* a row when looking for the
 *  top-most populated cell in the lieferschein-column "stack"
 *  (Bestellung above LS). Used by ArrowUp/Down recovery + by
 *  ``firstPopulatedCell`` to make a fresh canvas land at the
 *  visual top-left tile. */
const LIEFERSCHEIN_STACK: readonly ColumnKey[] = ['bestellung', 'lieferschein'] as const;

export interface TileRef {
  /** Source document id for the focused tile, drives the preview
   *  panel via ``selectedDocId``. ``null`` on tiles that don't carry
   *  a source doc (missing-doc placeholders, payout-statement /
   *  payout-advice tiles whose entity has no source doc id). */
  docId: string | null;
  /** ReactFlow node id — stable per render. Useful for scrolling the
   *  focused tile into view in a future iteration. */
  nodeId: string;
  column: ColumnKey;
  rowIndex: number;
}

export interface GridRow {
  bestellung: TileRef | null;
  lieferschein: TileRef | null;
  gutschrift: TileRef | null;
  abrechnung: TileRef | null;
  zahlungsavis: TileRef | null;
}

export interface Grid {
  rows: GridRow[];
}

export type Selection = TileRef | null;

export type NavKey =
  | 'ArrowUp'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'Escape';

/**
 * Find the first non-null cell in row-major order, scanning the
 * lieferschein-column stack (Bestellung-then-LS) before the
 * downstream columns. Used when:
 *   - the current selection is ``null`` (fresh canvas);
 *   - the current selection points at a row that no longer exists
 *     (rematerialise dropped it).
 * Returns ``null`` if every cell in every row is null (defensive —
 * an empty canvas wouldn't have triggered a keydown anyway, but the
 * reducer should never throw on an unexpected shape).
 */
function firstPopulatedCell(grid: Grid): TileRef | null {
  for (const row of grid.rows) {
    for (const column of LIEFERSCHEIN_STACK) {
      const cell = row[column];
      if (cell != null) return cell;
    }
    for (const column of COLUMN_ORDER) {
      if (column === 'lieferschein') continue; // already scanned in LIEFERSCHEIN_STACK
      const cell = row[column];
      if (cell != null) return cell;
    }
  }
  return null;
}

/** Top-most populated tile in row N's lieferschein-column stack
 *  (Bestellung above LS). Returns ``null`` when neither is present. */
function topOfLieferscheinStack(row: GridRow): TileRef | null {
  return row.bestellung ?? row.lieferschein;
}

/** Bottom-most populated tile in row N's lieferschein-column stack —
 *  the LS if present, else the Bestellung (defensive — a row with
 *  Bestellung but no LS is degenerate but the reducer should not crash). */
function bottomOfLieferscheinStack(row: GridRow): TileRef | null {
  return row.lieferschein ?? row.bestellung;
}

export function moveSelection(
  grid: Grid,
  current: Selection,
  key: NavKey,
): Selection {
  if (key === 'Escape') return null;

  // Fresh canvas — first arrow press lands on the top-left tile.
  if (current == null) return firstPopulatedCell(grid);

  // Defensive: selection from a previous render referring to a
  // rowIndex that no longer exists. Recover by landing on the first
  // populated cell rather than crashing or returning null (the
  // operator's flow shouldn't break on a refetch).
  if (current.rowIndex < 0 || current.rowIndex >= grid.rows.length) {
    return firstPopulatedCell(grid);
  }

  const currentRow = grid.rows[current.rowIndex];

  if (key === 'ArrowUp') {
    // #915 — Intra-row up from LS to Bestellung when Bestellung is
    // stacked above in the same column.
    if (current.column === 'lieferschein' && currentRow.bestellung != null) {
      return currentRow.bestellung;
    }
    // From Bestellung: walk to previous row's bottom-most tile in the
    // lieferschein-column stack (LS if present, else Bestellung).
    if (current.column === 'bestellung') {
      for (let r = current.rowIndex - 1; r >= 0; r--) {
        const cell = bottomOfLieferscheinStack(grid.rows[r]);
        if (cell != null) return cell;
      }
      return current;
    }
    // Other columns: walk previous rows, same column.
    for (let r = current.rowIndex - 1; r >= 0; r--) {
      const cell = grid.rows[r][current.column];
      if (cell != null) return cell;
    }
    return current;
  }

  if (key === 'ArrowDown') {
    // #915 — Intra-row down from Bestellung to LS when LS exists in
    // the same row.
    if (current.column === 'bestellung' && currentRow.lieferschein != null) {
      return currentRow.lieferschein;
    }
    // From Bestellung with no same-row LS, or from LS: walk to next
    // row's top-most tile in the lieferschein-column stack (Bestellung
    // if present, else LS). Symmetric to ArrowUp from Bestellung which
    // walks to previous row's bottom-most tile (LS-or-Bestellung).
    if (current.column === 'bestellung' || current.column === 'lieferschein') {
      for (let r = current.rowIndex + 1; r < grid.rows.length; r++) {
        const cell = topOfLieferscheinStack(grid.rows[r]);
        if (cell != null) return cell;
      }
      return current;
    }
    // Other columns: walk next rows, same column.
    for (let r = current.rowIndex + 1; r < grid.rows.length; r++) {
      const cell = grid.rows[r][current.column];
      if (cell != null) return cell;
    }
    return current;
  }

  // ArrowLeft / ArrowRight: walk the column order, skip null cells.
  // #915 — Bestellung is treated as the ``lieferschein`` column for
  // horizontal traversal (they share the same x position).
  const anchorColumn: Exclude<ColumnKey, 'bestellung'> =
    current.column === 'bestellung' ? 'lieferschein' : current.column;
  const colIdx = COLUMN_ORDER.indexOf(anchorColumn);
  if (colIdx < 0) return firstPopulatedCell(grid);
  const step = key === 'ArrowRight' ? 1 : -1;
  for (
    let c = colIdx + step;
    c >= 0 && c < COLUMN_ORDER.length;
    c += step
  ) {
    const cell = currentRow[COLUMN_ORDER[c]];
    if (cell != null) return cell;
  }
  return current;
}
